// Imports exported chats one after another, in the background, while the person keeps using the
// app. The queue and the parsed messages are kept encrypted on the device, so an import picks up
// where it stopped after a reload. The export itself is read only on the device. What reaches the
// server is end-to-end encrypted like any other message.

import { isPlanLimit } from "../api";
import type { Content, ImportedMessage, MediaRef } from "../content";
import { type FileMeta, MediaTooLargeError } from "../media";
import type { Store } from "../storage";
import { groupInputs, type InputFile, mimeOf, type OpenedExport, openInput } from "./export";
import { mediaKindOf, messageIds, type SenderMatch, toImported } from "./mapping";
import { SealedStore } from "./sealed";
import { NotAWhatsAppExport, parseWhatsApp, type WhatsAppMessage } from "./whatsapp";

export type ImportStatus =
  | "reading"
  | "setup"
  | "queued"
  | "running"
  | "stopped"
  | "needs_file"
  | "done"
  | "failed";

export type ImportItem = {
  id: string;
  /** The file or folder the person chose. */
  name: string;
  title: string;
  status: ImportStatus;
  error?: string;
  /** Everyone who wrote in the chat, as the export names them. */
  senders: string[];
  matches: Record<string, SenderMatch>;
  chatId?: string;
  /** Messages in the export. */
  total: number;
  /** Messages handled so far, sent or skipped. */
  done: number;
  sent: number;
  /** Messages already in the chat from an earlier import. */
  skipped: number;
  /** Files that came with the export, and how many of the chat's files went up or were missing. */
  mediaFiles: number;
  mediaUploaded: number;
  mediaMissing: number;
  /** SHA-256 of the chat text, to recognise the same export when it is added again. */
  fingerprint: string;
  addedAt: string;
  finishedAt?: string;
};

export type ImportState = {
  items: ImportItem[];
  /** Set when the queue stopped for a reason that needs the person, such as full storage. */
  halted: "storage_full" | null;
};

/** What the queue needs from the signed-in client. RhemaClient provides it. */
export interface ImportTarget {
  /** Ids of messages already imported into a chat, from any device and any member. */
  importedIds(chatId: string): Promise<Set<string>>;
  uploadImportMedia(chatId: string, file: Blob, meta: FileMeta): Promise<MediaRef>;
  sendImport(
    chatId: string,
    content: Extract<Content, { type: "imported" }>,
    opts: { clientId: string; attachments: string[] },
  ): Promise<void>;
}

export type ImportQueueOptions = {
  /** Milliseconds between batches, so a long chat does not flood the server. */
  pace?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => Date;
};

type ItemData = { messages: WhatsAppMessage[]; ids: string[] };

/**
 * At most this many messages, files and bytes of JSON go in one encrypted batch. The server takes
 * up to 128 KB of base64 ciphertext, which is about 96 KB of plaintext.
 */
export const BATCH_MESSAGES = 200;
export const BATCH_FILES = 32;
export const BATCH_BYTES = 60_000;
/** Longer messages are cut here. In UTF-16 units, so at most about 48 KB of UTF-8. */
const MAX_BODY = 16_000;
/** Room for a file's key, hash, name and size inside a batch. */
const MEDIA_REF_BYTES = 1_000;

function utf8Length(text: string): number {
  return new TextEncoder().encode(text).length;
}
const RETRIES = 5;

export const STORAGE_FULL =
  "Your Rhema storage is full, so the import stopped. Make room or upgrade your plan, then choose Resume.";
const OFFLINE = "Rhema could not reach the server. Check your connection, then try again.";

function uuidFrom(bytes: Uint8Array): string {
  const b = Array.from(bytes.subarray(0, 16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = b.map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function digest(text: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
}

async function fingerprintOf(text: string): Promise<string> {
  return Array.from(await digest(text))
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");
}

/** The same batch of the same import always gets the same client id, so a retry never doubles it. */
export async function batchClientId(importId: string, start: number): Promise<string> {
  return uuidFrom(await digest(`${importId}:${start}`));
}

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number }).status;
}

export class ImportQueue {
  private sealed: SealedStore;
  private sources = new Map<string, OpenedExport>();
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private stopRequested = new Set<string>();
  private restored: Promise<void> | null = null;
  private readonly pace: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly now: () => Date;
  state: ImportState = { items: [], halted: null };

  constructor(
    private readonly target: ImportTarget,
    store: Store,
    opts: ImportQueueOptions = {},
  ) {
    this.sealed = new SealedStore(store, "import:");
    this.pace = opts.pace ?? 800;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.now = opts.now ?? (() => new Date());
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  private emit(next: Partial<ImportState>) {
    this.state = { ...this.state, ...next };
    for (const l of this.listeners) l();
  }

  private async save() {
    await this.sealed.set("queue", this.state);
  }

  private async patch(id: string, patch: Partial<ImportItem>, persist = true) {
    this.emit({ items: this.state.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) });
    if (persist) await this.save();
  }

  item(id: string): ImportItem | undefined {
    return this.state.items.find((i) => i.id === id);
  }

  /** Loads the queue saved on this device and carries on with anything that was waiting. */
  restore(): Promise<void> {
    this.restored ??= (async () => {
      const saved = await this.sealed.get<ImportState>("queue");
      if (!saved) return;
      const items = saved.items.map((i): ImportItem => {
        if (i.status === "running") return { ...i, status: "queued" };
        if (i.status === "reading")
          return { ...i, status: "failed", error: "Rhema closed while reading this export. Add it again." };
        return i;
      });
      this.emit({
        items: [...items, ...this.state.items.filter((i) => !items.some((s) => s.id === i.id))],
        halted: saved.halted,
      });
      await this.save();
      this.kick();
    })();
    return this.restored;
  }

  /**
   * Adds exports the person chose, dropped or shared. Each one is read and parsed on the device,
   * then waits for the person to match its senders and pick a chat.
   */
  async add(inputs: InputFile[]): Promise<ImportItem[]> {
    await this.restore();
    const groups = groupInputs(inputs);
    const added: ImportItem[] = [];
    for (const group of groups) {
      const item: ImportItem = {
        id: crypto.randomUUID(),
        name: group.main.file.name,
        title: group.main.file.name,
        status: "reading",
        senders: [],
        matches: {},
        total: 0,
        done: 0,
        sent: 0,
        skipped: 0,
        mediaFiles: 0,
        mediaUploaded: 0,
        mediaMissing: 0,
        fingerprint: "",
        addedAt: this.now().toISOString(),
      };
      added.push(item);
      this.emit({ items: [...this.state.items, item] });
      try {
        const opened = await openInput(group);
        const parsed = parseWhatsApp(opened.text);
        const ids = await messageIds(parsed.messages);
        const fingerprint = await fingerprintOf(opened.text);
        this.sources.set(item.id, opened);
        await this.sealed.set(`data:${item.id}`, { messages: parsed.messages, ids } satisfies ItemData);
        await this.patch(item.id, {
          title: opened.title,
          status: "setup",
          senders: parsed.senders,
          total: parsed.messages.length,
          mediaFiles: opened.media.length,
          fingerprint,
        });
      } catch (err) {
        const error =
          err instanceof NotAWhatsAppExport
            ? "This is not a WhatsApp chat export. In WhatsApp, open the chat, choose Export chat, and add the file it makes."
            : (err as Error).message || "This export could not be read.";
        await this.patch(item.id, { status: "failed", error });
      }
    }
    return added.map((a) => this.item(a.id) ?? a);
  }

  /** Saves who each sender is and which chat to import into, then queues the import. */
  async start(id: string, setup: { matches: Record<string, SenderMatch>; chatId: string }): Promise<void> {
    await this.patch(id, {
      matches: setup.matches,
      chatId: setup.chatId,
      status: "queued",
      error: undefined,
    });
    this.kick();
  }

  /** Gives the queue the export again after a reload, so its photos and files can go up. */
  async reattach(id: string, inputs: InputFile[]): Promise<void> {
    const item = this.item(id);
    const group = groupInputs(inputs)[0];
    if (!item || !group) return;
    const opened = await openInput(group);
    if ((await fingerprintOf(opened.text)) !== item.fingerprint) {
      throw new Error("That is a different export. Add the same file you chose before.");
    }
    this.sources.set(id, opened);
    if (item.status === "needs_file") await this.patch(id, { status: "queued", error: undefined });
    this.kick();
  }

  /** Stops one import after the batch it is on. It can be resumed later. */
  async stop(id: string): Promise<void> {
    const item = this.item(id);
    if (!item) return;
    if (item.status === "running") this.stopRequested.add(id);
    else if (item.status === "queued") await this.patch(id, { status: "stopped" });
  }

  /** Puts a stopped or failed import, or the whole queue after full storage, back in line. */
  async resume(id?: string): Promise<void> {
    if (id) {
      const item = this.item(id);
      if (item && ["stopped", "failed"].includes(item.status) && item.chatId) {
        await this.patch(id, { status: "queued", error: undefined });
      }
    }
    if (this.state.halted) {
      this.emit({ halted: null });
      await this.save();
    }
    this.kick();
  }

  /** Takes an import off the list and forgets its messages. A running import stops first. */
  async remove(id: string): Promise<void> {
    if (this.item(id)?.status === "running") {
      this.stopRequested.add(id);
      await this.running;
    }
    this.sources.delete(id);
    this.emit({ items: this.state.items.filter((i) => i.id !== id) });
    await this.sealed.delete(`data:${id}`);
    await this.save();
  }

  /** Clears finished imports from the list. */
  async clearFinished(): Promise<void> {
    const finished = this.state.items.filter((i) => i.status === "done");
    for (const item of finished) await this.sealed.delete(`data:${item.id}`);
    this.emit({ items: this.state.items.filter((i) => i.status !== "done") });
    await this.save();
  }

  /** Resolves when the queue has nothing left it can do on its own. */
  idle(): Promise<void> {
    return this.running ?? Promise.resolve();
  }

  private kick() {
    if (this.running) return;
    this.running = (async () => {
      // Yield first, so the finally below never runs before this.running is assigned.
      await Promise.resolve();
      try {
        for (;;) {
          if (this.state.halted) return;
          const next = this.state.items.find((i) => i.status === "queued");
          if (!next) return;
          await this.runItem(next.id);
        }
      } finally {
        this.running = null;
      }
    })();
  }

  /** Runs a request, waiting out rate limits and retrying while the network is down. */
  private async attempt<T>(id: string, fn: () => Promise<T>): Promise<T> {
    let failures = 0;
    for (;;) {
      try {
        return await fn();
      } catch (err) {
        const status = statusOf(err);
        if (status === 429) {
          await this.sleep(30_000);
          continue;
        }
        if (status !== undefined && status < 500) throw err;
        failures += 1;
        if (failures > RETRIES || this.stopRequested.has(id))
          throw Object.assign(new Error(OFFLINE), { offline: true });
        await this.sleep(2 ** failures * 1000);
      }
    }
  }

  private async runItem(id: string): Promise<void> {
    const item = this.item(id);
    if (!item?.chatId) return;
    const chatId = item.chatId;
    const data = await this.sealed.get<ItemData>(`data:${id}`);
    if (!data) {
      await this.patch(id, {
        status: "failed",
        error: "This import's messages are no longer on this device. Add the export again.",
      });
      return;
    }
    const source = this.sources.get(id);
    const needsFiles = data.messages.slice(item.done).some((m) => m.attachment);
    if (needsFiles && item.mediaFiles > 0 && !source) {
      await this.patch(id, {
        status: "needs_file",
        error: "Add this export again to finish its photos and files. Rhema does not keep a copy of it.",
      });
      return;
    }
    await this.patch(id, { status: "running", error: undefined });

    try {
      const existing = await this.attempt(id, () => this.target.importedIds(chatId));
      let { done, sent, skipped, mediaUploaded, mediaMissing } = item;
      while (done < data.messages.length) {
        if (this.stopRequested.has(id)) {
          this.stopRequested.delete(id);
          await this.patch(id, { status: "stopped" });
          return;
        }
        const start = done;
        const batch: ImportedMessage[] = [];
        const attachments: string[] = [];
        let bytes = 0;
        while (
          done < data.messages.length &&
          batch.length < BATCH_MESSAGES &&
          attachments.length < BATCH_FILES &&
          bytes < BATCH_BYTES
        ) {
          const m = data.messages[done]!;
          const messageId = data.ids[done]!;
          if (existing.has(messageId)) {
            done += 1;
            skipped += 1;
            continue;
          }
          const out = toImported(m, messageId, item.matches);
          if (out.body && out.body.length > MAX_BODY) out.body = `${out.body.slice(0, MAX_BODY)}…`;
          // Measured in UTF-8 bytes, with room for a file's details, so the encrypted batch stays
          // under the server's limit. A message that does not fit starts the next batch.
          const size = utf8Length(JSON.stringify(out)) + (m.attachment ? MEDIA_REF_BYTES : 0);
          if (batch.length && bytes + size > BATCH_BYTES) break;
          done += 1;
          if (m.attachment) {
            const file = source ? await source.readMedia(m.attachment) : null;
            if (!file) {
              out.omitted = "media";
              mediaMissing += 1;
            } else {
              try {
                const mime = file.type || mimeOf(m.attachment);
                const ref = await this.attempt(id, () =>
                  this.target.uploadImportMedia(chatId, file, { mime, name: m.attachment }),
                );
                out.media = ref;
                out.media_kind = mediaKindOf(m.attachment, mime);
                attachments.push(ref.key);
                mediaUploaded += 1;
              } catch (err) {
                if (!(err instanceof MediaTooLargeError)) throw err;
                out.omitted = "media";
                mediaMissing += 1;
              }
            }
          }
          bytes += size;
          batch.push(out);
        }
        if (batch.length) {
          const clientId = await batchClientId(id, start);
          await this.attempt(id, () =>
            this.target.sendImport(
              chatId,
              { type: "imported", source: "whatsapp", import_id: id, messages: batch },
              { clientId, attachments },
            ),
          );
          sent += batch.length;
          for (const m of batch) existing.add(m.id);
        }
        await this.patch(id, { done, sent, skipped, mediaUploaded, mediaMissing });
        if (batch.length && done < data.messages.length) await this.sleep(this.pace);
      }
      this.sources.delete(id);
      await this.patch(id, { status: "done", finishedAt: this.now().toISOString() });
    } catch (err) {
      this.stopRequested.delete(id);
      if (isPlanLimit(err) && err.code === "storage_full") {
        await this.patch(id, { status: "queued", error: STORAGE_FULL });
        this.emit({ halted: "storage_full" });
        await this.save();
        return;
      }
      await this.patch(id, { status: "failed", error: (err as Error).message || OFFLINE });
    }
  }
}
